import { ChevronDown, ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Badge } from '@/shared/components/ui/badge';

interface CollapsibleSectionProps {
  title: string;
  count?: number;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  icon?: LucideIcon;
  iconClassName?: string;
  'data-testid'?: string;
}

export function CollapsibleSection({
  title,
  count,
  isOpen,
  onToggle,
  children,
  icon: Icon,
  iconClassName,
  'data-testid': testId,
}: CollapsibleSectionProps) {
  return (
    <div className="rounded-lg border bg-card" data-testid={testId}>
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'flex w-full items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-accent/50',
          isOpen ? 'rounded-t-lg' : 'rounded-lg',
        )}
        aria-expanded={isOpen}
      >
        {Icon && (
          <Icon className={cn('h-4 w-4 shrink-0', iconClassName ?? 'text-muted-foreground')} />
        )}
        <span className="text-sm font-semibold">{title}</span>
        {count !== undefined && (
          <Badge variant="destructive" className="text-xs">
            {count}
          </Badge>
        )}
        <span className="flex-1" />
        {isOpen ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </button>
      {isOpen && <div className="border-t p-4">{children}</div>}
    </div>
  );
}
